import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, X, Loader2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { getStoredUser } from "./RegisterModal";
import { routeVoiceIntent } from "../lib/voiceIntent";

/**
 * Full-screen Siri-style voice capture overlay.
 * Tap once → permission asked → recording starts immediately with visual feedback
 * Auto-stops after ~1.6s of silence (VAD via AnalyserNode RMS)
 * Hard cap at 30s. Then transcribes and sends user straight to the assistant
 * with the transcribed text auto-submitted as the first message (via ?q=...).
 */
const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/ogg;codecs=opus",
  "audio/ogg",
];

function pickMime() {
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of MIME_CANDIDATES) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch {/* noop */}
  }
  return null;
}

function extFor(mime) {
  if (!mime) return "webm";
  if (mime.includes("mp4")) return "m4a";
  if (mime.includes("ogg")) return "ogg";
  return "webm";
}

const SILENCE_MS = 1600;       // stop after this much silence
const MIN_SPEECH_MS = 500;     // require at least this much before allowing auto-stop
const HARD_CAP_MS = 30000;     // 30s max
const SILENCE_RMS = 0.018;     // RMS threshold below which we consider silence

export default function VoiceCapture({ open, onClose }) {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const navigate = useNavigate();

  const [phase, setPhase] = useState("idle"); // idle | requesting | recording | transcribing | error | done
  const [error, setError] = useState("");
  const [level, setLevel] = useState(0);

  const streamRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const silenceStartRef = useRef(null);
  const speechStartRef = useRef(null);
  const hardCapRef = useRef(null);
  const cancelledRef = useRef(false);
  const stoppedRef = useRef(false);

  const cleanup = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (hardCapRef.current) { clearTimeout(hardCapRef.current); hardCapRef.current = null; }
    if (analyserRef.current) { try { analyserRef.current.disconnect(); } catch {/* noop */} analyserRef.current = null; }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {/* noop */} audioCtxRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tk) => { try { tk.stop(); } catch {/* noop */} });
      streamRef.current = null;
    }
    silenceStartRef.current = null;
    speechStartRef.current = null;
  };

  const stopRec = (reason = "manual") => {
    if (stoppedRef.current) return;
    stoppedRef.current = true;
    if (reason === "cancel") cancelledRef.current = true;
    const r = recRef.current;
    try { if (r && r.state === "recording") r.stop(); } catch {/* noop */}
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  };

  // Reset and start when opened
  useEffect(() => {
    if (!open) return;
    cancelledRef.current = false;
    stoppedRef.current = false;
    chunksRef.current = [];
    setPhase("requesting");
    setError("");
    setLevel(0);
    (async () => {
      try {
        if (typeof window === "undefined" || !window.isSecureContext) {
          throw new Error(pt ? "Microfone exige HTTPS." : "Mic requires HTTPS.");
        }
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error(pt ? "Navegador sem suporte. Use Chrome ou Safari." : "Browser unsupported. Use Chrome/Safari.");
        }
        if (typeof MediaRecorder === "undefined") {
          throw new Error(pt ? "Gravação não suportada neste navegador." : "Recording not supported.");
        }
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          });
        } catch (e) {
          const name = e?.name || "";
          if (name === "NotAllowedError" || name === "PermissionDeniedError") {
            throw new Error(pt ? "Permita o microfone nas configurações do navegador e tente de novo." : "Allow mic in browser settings and try again.");
          }
          if (name === "NotFoundError") {
            throw new Error(pt ? "Nenhum microfone encontrado." : "No mic found.");
          }
          throw new Error(pt ? "Não consegui acessar o microfone." : "Couldn't access mic.");
        }
        streamRef.current = stream;

        // Wire MediaRecorder
        const mime = pickMime();
        let rec;
        try { rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream); }
        catch { throw new Error(pt ? "Formato de áudio não suportado." : "Audio format unsupported."); }
        recRef.current = rec;
        rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
        rec.onerror = () => {
          setError(pt ? "Erro na gravação. Tente de novo." : "Recording error. Try again.");
          setPhase("error");
          cleanup();
        };
        rec.onstop = async () => {
          if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
          if (cancelledRef.current) {
            cleanup();
            return;
          }
          const ext = extFor(rec.mimeType || mime);
          const blobType = (rec.mimeType || mime || "audio/webm").split(";")[0];
          const blob = new Blob(chunksRef.current, { type: blobType });
          cleanup();
          if (blob.size < 1500) {
            setError(pt ? "Não captei sua voz. Fale um pouco mais alto e por mais tempo." : "Didn't catch your voice. Speak a bit louder and longer.");
            setPhase("error");
            return;
          }
          setPhase("transcribing");
          try {
            const form = new FormData();
            form.append("audio", blob, `speech.${ext}`);
            form.append("lang", lang);
            const email = getStoredUser()?.email;
            if (email) form.append("email", email);
            const res = await api.post("/ai/transcribe", form, {
              headers: { "Content-Type": "multipart/form-data" },
              timeout: 60000,
            });
            const text = (res.data?.text || "").trim();
            if (!text) {
              setError(pt ? "Não entendi o áudio. Tente de novo mais devagar." : "Couldn't understand. Try again, slower.");
              setPhase("error");
              return;
            }
            setPhase("done");
            // Route the voice question to the right page based on intent.
            // Examples: "quero falar com o coach" → /coach?q=...
            //           "compare bitcoin e S&P 500"  → /comparador?q=...
            //           "quanto está o dólar"        → /mercado?q=...
            //           "devo investir em imóveis"   → /assistente?q=... (default)
            const { path, query } = routeVoiceIntent(text);
            const q = encodeURIComponent(query || text);
            navigate(`${path}?q=${q}`);
            onClose?.();
          } catch (err) {
            console.error("transcribe", err);
            if (err?.response?.status === 402 && err?.response?.data?.detail) {
              window.dispatchEvent(new CustomEvent("goldora:quota-blocked", { detail: { ...err.response.data.detail, feature: "transcribe" } }));
              onClose?.();
            } else {
              setError(pt ? "Falha ao transcrever. Tente novamente." : "Transcription failed. Try again.");
              setPhase("error");
            }
          }
        };

        // VAD via AnalyserNode
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        analyserRef.current = analyser;
        src.connect(analyser);
        const buf = new Float32Array(analyser.fftSize);
        const startedAt = Date.now();
        const tick = () => {
          if (!analyserRef.current) return;
          analyser.getFloatTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
          const rms = Math.sqrt(sum / buf.length);
          setLevel(Math.min(1, rms * 9));
          const now = Date.now();
          if (rms > SILENCE_RMS) {
            silenceStartRef.current = null;
            if (!speechStartRef.current) speechStartRef.current = now;
          } else {
            if (!silenceStartRef.current) silenceStartRef.current = now;
            const silentFor = now - silenceStartRef.current;
            const spokeFor = speechStartRef.current ? now - speechStartRef.current : 0;
            if (spokeFor > MIN_SPEECH_MS && silentFor > SILENCE_MS) {
              stopRec("silence");
              return;
            }
          }
          if (now - startedAt > HARD_CAP_MS) {
            stopRec("hardcap");
            return;
          }
          rafRef.current = requestAnimationFrame(tick);
        };

        try { rec.start(); }
        catch { throw new Error(pt ? "Não consegui iniciar a gravação." : "Couldn't start recording."); }

        setPhase("recording");
        rafRef.current = requestAnimationFrame(tick);
        hardCapRef.current = setTimeout(() => stopRec("hardcap"), HARD_CAP_MS + 250);
      } catch (e) {
        setError(e.message || (pt ? "Erro inesperado." : "Unexpected error."));
        setPhase("error");
        cleanup();
      }
    })();

    return () => {
      // On unmount or close — bail out cleanly
      cancelledRef.current = true;
      stopRec("cancel");
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const handleClose = () => {
    cancelledRef.current = true;
    stopRec("cancel");
    cleanup();
    onClose?.();
  };

  const handleStopNow = () => {
    if (phase === "recording") stopRec("manual");
  };

  const orbScale = 1 + level * 0.6;
  const ringSize = 240;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl flex flex-col animate-in fade-in duration-200"
      data-testid="voice-capture-overlay"
      role="dialog"
      aria-modal="true"
    >
      <button
        onClick={handleClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/5 border border-white/10 text-neutral-400 hover:text-white hover:bg-white/10"
        data-testid="voice-capture-close"
        aria-label="close"
      >
        <X size={20} />
      </button>

      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {/* Status label */}
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold-400 mb-3">
          {phase === "requesting" && (pt ? "Liberando microfone..." : "Requesting mic...")}
          {phase === "recording" && (pt ? "Estou ouvindo" : "I'm listening")}
          {phase === "transcribing" && (pt ? "Transcrevendo" : "Transcribing")}
          {phase === "error" && (pt ? "Tivemos um problema" : "We had a problem")}
        </div>

        {/* Pulsing orb */}
        <div className="relative grid place-items-center" style={{ width: ringSize, height: ringSize }}>
          {phase === "recording" && (
            <>
              <span
                className="absolute inset-0 rounded-full"
                style={{
                  background: "radial-gradient(circle, rgba(233,185,52,0.25) 0%, rgba(233,185,52,0) 70%)",
                  transform: `scale(${orbScale})`,
                  transition: "transform 60ms linear",
                }}
              />
              <span className="absolute inset-6 rounded-full border border-gold-500/30 animate-ping" style={{ animationDuration: "2.4s" }} />
            </>
          )}
          <div
            className={`relative w-32 h-32 rounded-full grid place-items-center shadow-[0_8px_40px_rgba(233,185,52,0.35)] border ${
              phase === "error" ? "bg-red-500/20 border-red-500/40 text-red-300" :
              phase === "transcribing" ? "bg-white/5 border-white/10 text-gold-300" :
              "bg-gradient-to-br from-gold-400 to-gold-600 border-gold-300/60 text-black"
            }`}
            style={phase === "recording" ? { transform: `scale(${1 + level * 0.15})`, transition: "transform 60ms linear" } : {}}
            data-testid="voice-capture-orb"
          >
            {phase === "transcribing" ? (
              <Loader2 size={42} className="animate-spin" />
            ) : phase === "error" ? (
              <AlertCircle size={42} />
            ) : (
              <Mic size={42} strokeWidth={2.2} />
            )}
          </div>
        </div>

        {/* Hint / error text */}
        <div className="mt-8 text-center max-w-sm">
          {phase === "recording" && (
            <p className="text-neutral-300 text-sm">
              {pt ? "Fale sua pergunta. Eu paro sozinho quando você terminar." : "Ask your question. I'll stop when you're done."}
            </p>
          )}
          {phase === "requesting" && (
            <p className="text-neutral-400 text-sm">
              {pt ? "Toque em \"Permitir\" no aviso do navegador." : "Tap \"Allow\" on the browser prompt."}
            </p>
          )}
          {phase === "transcribing" && (
            <p className="text-neutral-400 text-sm">
              {pt ? "Convertendo sua voz em texto..." : "Converting your voice to text..."}
            </p>
          )}
          {phase === "error" && (
            <p className="text-red-300 text-sm" data-testid="voice-capture-error">
              {error}
            </p>
          )}
        </div>

        {/* Action buttons */}
        <div className="mt-10 flex items-center gap-3">
          {phase === "recording" && (
            <button
              onClick={handleStopNow}
              data-testid="voice-capture-stop"
              className="px-6 py-3 rounded-full bg-gold-500 hover:bg-gold-400 text-black font-display tracking-wide text-sm transition-colors"
            >
              {pt ? "Pronto, parar" : "Done, stop"}
            </button>
          )}
          {phase === "error" && (
            <>
              <button
                onClick={() => { setPhase("idle"); setError(""); setTimeout(() => onClose?.(), 0); }}
                className="px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-neutral-300 hover:text-white text-sm"
                data-testid="voice-capture-cancel-error"
              >
                {pt ? "Fechar" : "Close"}
              </button>
              <button
                onClick={() => {
                  // re-trigger by closing and re-opening from parent isn't trivial; toggle phase to retry
                  setError("");
                  setPhase("requesting");
                  // simulate re-open
                  cancelledRef.current = false;
                  stoppedRef.current = false;
                  chunksRef.current = [];
                  // Re-run effect by cycling open prop is parent's job; here we just close and let user tap again.
                  onClose?.();
                }}
                className="px-5 py-2.5 rounded-full bg-gold-500 hover:bg-gold-400 text-black text-sm font-display"
                data-testid="voice-capture-retry"
              >
                {pt ? "Tentar de novo" : "Try again"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
